// Full console capture (log / info / warn) — opt-in per site.
//
// Complements errors.ts (which already covers uncaught errors, promise
// rejections and failed network calls): with this module on, a session's
// raw R2 record also contains what the page *printed*, which is what a bug
// investigation actually reads. console.error is intentionally not patched —
// sites that console.error also tend to throw (covered by errors.ts), and
// double-reporting the same failure as both `err` and `con` muddies the
// timeline.
//
// Safety properties, in order of importance:
//   1. Never break the host console — the original fn always runs, first,
//      inside its own try. Capture failures are swallowed.
//   2. Never capture our own output — "[hotmaps]"/"[hm-loader]" prefixed
//      lines are skipped, and a re-entrancy guard stops any loop where
//      serialization itself logs.
//   3. Hard budget per session (shared across levels) + per-message
//      truncation, so a render-loop logger can't flood batches.

import type { ConsoleEvent } from "@hotmaps/shared/events";

export type ConsoleEmit = (ev: ConsoleEvent) => void;

const CONSOLE_BUDGET = 200;
const MSG_MAX = 500;
const OWN_PREFIXES = ["[hotmaps]", "[hm-loader]", "[nagisot]"];

const LEVELS: Array<"log" | "info" | "warn"> = ["log", "info", "warn"];

export function registerConsoleCapture(sessionStart: number, emit: ConsoleEmit) {
  let budget = CONSOLE_BUDGET;
  let inCapture = false;

  for (const level of LEVELS) {
    const orig = console[level];
    if (typeof orig !== "function") continue;
    const bound = orig.bind(console);
    console[level] = function (...args: unknown[]) {
      bound(...args);
      if (inCapture || budget <= 0) return;
      inCapture = true;
      try {
        const message = serializeArgs(args);
        if (message && !isOwnLine(message)) {
          budget--;
          emit({
            t: "con",
            ts: Date.now() - sessionStart,
            level,
            message,
          });
        }
      } catch {
        /* capture must never throw into the host page */
      } finally {
        inCapture = false;
      }
    };
  }
}

function isOwnLine(message: string): boolean {
  for (const p of OWN_PREFIXES) if (message.startsWith(p)) return true;
  return false;
}

function serializeArgs(args: unknown[]): string {
  const parts: string[] = [];
  let total = 0;
  for (const a of args) {
    if (total >= MSG_MAX) break;
    const s = serializeOne(a);
    parts.push(s);
    total += s.length + 1;
  }
  const joined = parts.join(" ");
  return joined.length > MSG_MAX ? joined.slice(0, MSG_MAX) + "…" : joined;
}

function serializeOne(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null || typeof v === "number" || typeof v === "boolean") return String(v);
  if (v instanceof Error) return v.name + ": " + v.message;
  try {
    // Depth-limited JSON: enough to identify the object, cheap to compute.
    const s = JSON.stringify(v, jsonSafeReplacer());
    return s == null ? Object.prototype.toString.call(v) : s.slice(0, 200);
  } catch {
    return Object.prototype.toString.call(v);
  }
}

// Replacer that survives circular refs and DOM nodes without walking them.
function jsonSafeReplacer(): (k: string, v: unknown) => unknown {
  const seen = new WeakSet<object>();
  return (_k, v) => {
    if (typeof v === "function") return "[fn]";
    if (v && typeof v === "object") {
      if (seen.has(v as object)) return "[circular]";
      seen.add(v as object);
      if (typeof Node !== "undefined" && v instanceof Node) {
        return "<" + (v as Element).nodeName?.toLowerCase?.() + ">";
      }
    }
    return v;
  };
}
